import { FC, useCallback, useEffect, useState } from 'react';
import { BetaTourStep, getAvailableSteps, hasSeenBetaTour, markBetaTourSeen } from './betaTourSteps';
import BetaWelcome from './BetaWelcome';
import BetaTourStepBubble from './BetaTourStepBubble';
import BetaTourSpotlight from './BetaTourSpotlight';
import BetaChip from './BetaChip';
import { flyTourCardToChip, prefersReducedMotion } from './flyTourCardToChip';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { isUserAdmin } from '../../businessLogicService/isUserAdmin';

type Stage = 'welcome' | 'running' | 'idle';

export const BetaTour: FC = () => {
  const userRedux = useAppSelector(selectUser);
  const isAdmin = !!userRedux && isUserAdmin(userRedux);
  const [stage, setStage] = useState<Stage>(() => (hasSeenBetaTour() ? 'idle' : 'welcome'));
  const [steps, setSteps] = useState<BetaTourStep[]>([]);
  const [index, setIndex] = useState(0);
  const [flying, setFlying] = useState(false);

  const currentStep = stage === 'running' ? steps[index] : undefined;
  const anchor = currentStep ? document.querySelector<HTMLElement>(currentStep.anchorSelector) : null;

  useEffect(() => {
    if (stage === 'welcome') markBetaTourSeen();
  }, [stage]);

  const finish = useCallback(() => {
    setFlying(false);
    setStage('idle');
  }, []);

  const start = useCallback(() => {
    const available = getAvailableSteps(document, isAdmin);

    if (!available.length) {
      finish();
      return;
    }

    setSteps(available);
    setIndex(0);
    setStage('running');
  }, [finish]);

  const next = useCallback(() => {
    if (index + 1 >= steps.length) {
      finish();
      return;
    }
    setIndex(index + 1);
  }, [index, steps.length, finish]);

  const skip = useCallback(() => {
    if (flying) return;
    if (prefersReducedMotion()) {
      finish();
      return;
    }
    setFlying(true);
  }, [flying, finish]);

  useEffect(() => {
    if (!flying) return;

    const card = document.querySelector<HTMLElement>('.nc_beta-tour__bubble');
    const chip = document.querySelector<HTMLElement>('.nc_beta-chip__toggle');

    if (!card || !chip) {
      finish();
      return;
    }

    return flyTourCardToChip(card, chip, finish);
  }, [flying, finish]);

  useEffect(() => {
    if (stage !== 'running') return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') skip();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [stage, skip]);

  return (
    <>
      <BetaChip onReplay={start} />

      {stage === 'welcome' && <BetaWelcome isAdmin={isAdmin} onStart={start} onDismiss={finish} />}

      {currentStep && anchor && (
        <>
          {!flying && <BetaTourSpotlight anchor={anchor} />}
          <BetaTourStepBubble
            anchor={anchor}
            stepNumber={index + 1}
            stepCount={steps.length}
            title={currentStep.title}
            body={currentStep.body}
            onNext={next}
            onSkip={skip}
          />
        </>
      )}
    </>
  );
};

export default BetaTour;
